import {
  CircleXIcon,
  RefreshCcwIcon,
  ShieldCheckIcon,
  Trash2Icon,
} from 'lucide-react';
import { Badge, Button, Card, CopyButton } from '@tinct/web-components';
import { cn } from '@tinct/core-utils';
import type {
  LandingPageDomainDnsRecord,
  LandingPageDomainStatus,
} from '@tinct/core-api-models';

export type DomainProvisioningCardProps = {
  className?: string;
  domain: string;
  status: LandingPageDomainStatus;
  errorMessage?: string | null;
  dnsRecords: LandingPageDomainDnsRecord[];
  isChecking?: boolean;
  isRetrying?: boolean;
  onCheckNow: () => void;
  onRetry: () => void;
  onRemove: () => void;
};

const statusLabels: Record<Exclude<LandingPageDomainStatus, 'active'>, string> = {
  pending_dns: 'Waiting for DNS',
  pending_validation: 'Issuing certificate',
  issued: 'Activating',
  failed: 'Failed',
};

/**
 * The in-progress states of domain provisioning: hand off the CNAME record
 * (pending_dns), then report certificate issuance progress
 * (pending_validation / issued), or surface the terminal failure with a
 * retry. The server poller keeps advancing even with this screen closed.
 */
export function DomainProvisioningCard({
  className,
  domain,
  status,
  errorMessage,
  dnsRecords,
  isChecking,
  isRetrying,
  onCheckNow,
  onRetry,
  onRemove,
}: DomainProvisioningCardProps) {
  const isFailed = status === 'failed';

  return (
    <Card className={cn('gap-0 py-0', className)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-6 pt-6">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-3">
            <h3 className="font-heading text-xl font-semibold leading-7 tracking-[-0.3px] text-foreground">
              {domain}
            </h3>
            <Badge variant={isFailed ? 'destructive' : 'secondary'}>
              {statusLabels[status as Exclude<LandingPageDomainStatus, 'active'>] ?? status}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Once active, every published page of this workspace is served under
            this domain — nothing to configure per page, ever.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-6 px-6 py-6">
        {/* Failure */}
        {isFailed && (
          <div className="flex items-start gap-4 rounded-2xl border border-rose-100 bg-rose-50 p-4">
            <div className="pt-0.5">
              <CircleXIcon className="size-4 text-destructive" />
            </div>
            <div className="flex flex-1 flex-col gap-2 text-sm leading-5 text-destructive">
              <span className="font-medium">Provisioning failed</span>
              <span>
                {errorMessage ||
                  'Something went wrong while provisioning your domain.'}
              </span>
            </div>
          </div>
        )}

        {/* Certificate progress */}
        {(status === 'pending_validation' || status === 'issued') && (
          <div className="flex items-center gap-3 rounded-2xl border border-teal-100 bg-[var(--colors-success-background)] p-4 text-sm text-teal-600">
            <ShieldCheckIcon className="size-4 shrink-0" />
            <span>
              {status === 'pending_validation'
                ? 'DNS detected, issuing certificate… This can take from a few minutes to a few hours.'
                : 'Certificate issued, activating…'}
            </span>
          </div>
        )}

        {/* DNS record hand-off */}
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {status === 'pending_dns'
              ? 'Publish this record with your DNS provider, then check. This single record covers every landing page of the workspace, forever.'
              : 'The record below must stay published with your DNS provider.'}
          </p>
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Value</th>
                </tr>
              </thead>
              <tbody>
                {dnsRecords.map((record, index) => (
                  <tr key={index} className="border-b last:border-b-0">
                    <td className="px-4 py-2 font-mono text-xs">
                      {record.type}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1">
                        <span className="break-all font-mono text-xs">
                          {record.name}
                        </span>
                        {record.name && <CopyButton value={record.name} />}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1">
                        <span className="break-all font-mono text-xs">
                          {record.value}
                        </span>
                        {record.value && <CopyButton value={record.value} />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 border-t px-6 py-6">
        <Button variant="outline" onClick={onRemove}>
          <Trash2Icon className="size-4" />
          Remove domain
        </Button>
        {isFailed ? (
          <Button onClick={onRetry} isLoading={isRetrying}>
            <RefreshCcwIcon className="size-4" />
            Retry
          </Button>
        ) : (
          <Button onClick={onCheckNow} isLoading={isChecking}>
            {!isChecking && <RefreshCcwIcon className="size-4" />}
            Check now
          </Button>
        )}
      </div>
    </Card>
  );
}
